import axios from "axios";
import { baseURL } from "../apirest";

const handleMoodleResponse = (data) => {
  if (data && (data.exception || data.errorcode)) {
    throw new Error(data.message || "Moodle no pudo completar la solicitud.");
  }
  return data;
};

const requireToken = (token) => {
  if (!token) throw new Error("Debes iniciar sesión para acceder al foro.");
  return token;
};

const request = async (name, token, values = {}, write = false) => {
  const params = new URLSearchParams({
    wstoken: requireToken(token), moodlewsrestformat: "json",
    wsfunction: `local_forum_tipddy_${name}`, ...values,
  });
  try {
    const response = write
      ? await axios.post(`${baseURL}webservice/rest/server.php`, params)
      : await axios.get(`${baseURL}webservice/rest/server.php`, { params });
    return handleMoodleResponse(response.data);
  } catch (error) {
    if (error.response || error.request) throw new Error("No se pudo conectar con el foro. Inténtalo nuevamente.");
    throw error;
  }
};

export const getForumCategories = ({ token }) => request("get_categories", token);
export const getForumThemes = ({ token }) => request("get_themes", token);
export const getForumBackend = ({ token }) => request("get_backend", token);
export const getForumDiscussions = ({ token, categoryId }) => request("get_discussions", token, { category_id: categoryId });
export const getForumPosts = ({ token, discussionId }) => request("get_posts", token, { discussion_id: discussionId });
export const getLatestPosts = ({ token, limit = 5 }) => request("get_latest_posts", token, { limit });
export const searchForumPosts = ({ token, query, limit = 10 }) => request("search_posts", token, { query, limit });
export const deleteForumDiscussion = ({ token, discussionId }) => request("delete_discussion", token, { discussion_id: discussionId }, true);
export const togglePinDiscussion = ({ token, discussionId }) => request("toggle_pin", token, { discussion_id: discussionId }, true);
export const togglePinPost = ({ token, postId }) => request("toggle_pin_post", token, { post_id: postId }, true);
export const incrementDiscussionView = ({ token, discussionId }) => request("increment_view", token, { discussion_id: discussionId }, true);

export const validateForumFiles = (files, policy) => {
  if (!files.length) return;
  if (!policy) throw new Error("No se pudo obtener el límite de archivos de Moodle.");
  if (files.length > policy.maxfiles) throw new Error(`Puedes adjuntar hasta ${policy.maxfiles} archivos.`);
  const names = new Set();
  for (const file of files) {
    const extension = file.name.split(".").pop().toLowerCase();
    if (!policy.extensions.includes(extension)) throw new Error(`Formato no permitido: ${file.name}`);
    if (!file.size) throw new Error(`El archivo está vacío: ${file.name}`);
    if (policy.maxbytes > 0 && file.size > policy.maxbytes) throw new Error(`El archivo supera el límite de Moodle: ${file.name}`);
    if (names.has(file.name)) throw new Error("No puedes adjuntar dos archivos con el mismo nombre.");
    names.add(file.name);
  }
};

const publish = async (name, token, values, files = [], onProgress) => {
  requireToken(token);
  let draftId = 0;
  try {
    if (files.length) {
      const { attachment_policy: policy } = await getForumBackend({ token });
      validateForumFiles(files, policy);
      for (let index = 0; index < files.length; index += 1) {
        const body = new FormData();
        body.append("token", token);
        body.append("filepath", "/");
        body.append("itemid", String(draftId));
        body.append("file", files[index]);
        const { data } = await axios.post(`${baseURL}webservice/upload.php`, body, {
          onUploadProgress: (event) => {
            if (onProgress) onProgress(Math.round(100 * (index + (event.total ? event.loaded / event.total : 0)) / files.length));
          },
        });
        handleMoodleResponse(data);
        const uploaded = data?.[0];
        if (!uploaded || uploaded.error || !Number(uploaded.itemid)) {
          throw new Error("Moodle no pudo subir el archivo. Revisa los permisos de subida del servicio web.");
        }
        draftId = Number(uploaded.itemid);
      }
    }
    return await request(name, token, { ...values, draft_id: String(draftId) }, true);
  } catch (error) {
    if (error.response || error.request) throw new Error("No se pudo publicar el mensaje y sus adjuntos. Inténtalo nuevamente.");
    throw error;
  } finally {
    // Keep local File objects on error; retries create a fresh draft instead of duplicating uploads.
    if (draftId) await request("discard_attachment_draft", token, { draft_id: String(draftId) }, true).catch(() => {});
  }
};

export const createForumDiscussion = ({ token, categoryId, title, content, isPinned = false, authorId, groupId = 0, files = [], onProgress }) =>
  publish("create_discussion", token, {
    category_id: String(categoryId), title: (title || "").trim(), content: (content || "").trim(),
    is_pinned: isPinned ? "1" : "0", author_id: String(authorId || 0), group_id: String(groupId),
  }, files, onProgress);

export const createForumPost = ({ token, discussionId, content, authorId, parentId = 0, files = [], onProgress }) =>
  publish("create_post", token, {
    discussion_id: String(discussionId), content: (content || "").trim(),
    author_id: String(authorId || 0), parent_id: String(parentId),
  }, files, onProgress);

/** Fetch private attachments only from this Moodle installation, never a third-party viewer. */
export const getForumAttachment = async ({ token, fileurl, signal }) => {
  const root = new URL(baseURL, window.location.origin);
  const url = new URL(fileurl, root);
  const prefix = root.pathname.replace(/\/$/, "");
  if (url.origin !== root.origin || ![`${prefix}/pluginfile.php/`, `${prefix}/webservice/pluginfile.php/`].some((path) => url.pathname.startsWith(path))) {
    throw new Error("La dirección del adjunto no pertenece a Moodle.");
  }
  if (url.pathname.startsWith(`${prefix}/pluginfile.php/`)) {
    url.pathname = `${prefix}/webservice${url.pathname.slice(prefix.length)}`;
  }
  url.searchParams.set("token", requireToken(token));
  try {
    const { data } = await axios.get(url.toString(), { responseType: "blob", signal });
    if (data.type?.includes("json") || data.type?.includes("html")) throw new Error("El adjunto no está disponible o no tienes permiso para verlo.");
    return data;
  } catch (error) {
    if (error.name === "CanceledError" || error.name === "AbortError") throw error;
    throw new Error("No se pudo cargar el adjunto. Comprueba tus permisos e inténtalo nuevamente.");
  }
};
